import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { TextField, Button } from "@mui/material";
import { api } from "../api";  // <-- Correct API client
import usePageTitle from "../hooks/usePageTitle";
import "./auth.css";

const Register = () => {
  usePageTitle("Register");
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const [message, setMessage] = useState("");
  const [validation, setValidation] = useState({
    letters: false,
    number: false,
    special: false,
    match: false,
    emailValid: false,
  });

  const navigate = useNavigate();

  // ✅ Live validation on password and email
  useEffect(() => {
    const { password, confirmPassword, email } = formData;

    const hasFiveLetters = (password.match(/[A-Za-z]/g) || []).length >= 5;
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[^A-Za-z0-9]/.test(password);
    const match = password === confirmPassword && password.length > 0;
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

    setValidation({
      letters: hasFiveLetters,
      number: hasNumber,
      special: hasSpecial,
      match,
      emailValid,
    });
  }, [formData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    const allValid = Object.values(validation).every(Boolean);
    if (!allValid) {
      setMessage("Please meet all registration requirements.");
      return;
    }

    try {
      const response = await api.post("/api/register", {
        username: formData.username.trim(),
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        confirmPassword: formData.confirmPassword,
      });

      if (response.status === 200 || response.status === 201) {
        setMessage("Registration successful! Redirecting...");
        setTimeout(() => navigate("/login"), 2000);
      }
    } catch (error) {
      const backendMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        "";

      if (
        backendMessage.toLowerCase().includes("username") ||
        backendMessage.toLowerCase().includes("email") ||
        backendMessage.toLowerCase().includes("already")
      ) {
        setMessage(backendMessage);
      } else {
        setMessage("Error registering user.");
      }
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-box">
        <div className="auth-sidebar">
          <h3>Join Us!</h3>
          <p>Create an account to start your collection.</p>
        </div>

        <div className="auth-form">
          <h2>Sign Up</h2>
          <form onSubmit={handleSubmit}>
            <TextField
              label="Username"
              fullWidth
              size="small"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              sx={{ mb: 0.8 }}
            />
            <TextField
              label="Email"
              fullWidth
              size="small"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              error={formData.email.length > 0 && !validation.emailValid}
              helperText={
                formData.email.length > 0 && !validation.emailValid
                  ? "Enter a valid email address"
                  : " "
              }
              sx={{ mb: 0.8 }}
            />
            <TextField
              label="Password"
              fullWidth
              size="small"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              sx={{ mb: 0.8 }}
            />
            <TextField
              label="Confirm Password"
              fullWidth
              size="small"
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              sx={{ mb: 0.4 }}
            />

            {/* ✅ Live Feedback */}
            <ul style={{ listStyle: "none", padding: 0, fontSize: "0.84rem", marginTop: "4px", marginBottom: "6px", lineHeight: 1.25 }}>
              <li style={{ color: validation.letters ? "green" : "red" }}>
                {validation.letters ? "✅" : "❌"} At least 5 letters
              </li>
              <li style={{ color: validation.number ? "green" : "red" }}>
                {validation.number ? "✅" : "❌"} Includes a number
              </li>
              <li style={{ color: validation.special ? "green" : "red" }}>
                {validation.special ? "✅" : "❌"} Includes a special character
              </li>
              <li style={{ color: validation.match ? "green" : "red" }}>
                {validation.match ? "✅" : "❌"} Passwords match
              </li>
            </ul>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              className="auth-button"
              sx={{ mt: 0.6 }}
            >
              SIGN UP
            </Button>
          </form>

          <p className="message">{message}</p>
          <p style={{ marginTop: 10 }}>
            Already have an account? <Link to="/login">Log in here</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;

